import { useQuery } from "@tanstack/react-query";
import { ExternalLink, FileText, Landmark } from "lucide-react";
import { useState } from "react";
import { DataLoadError } from "~/components/ui/data-load-error";
import { envConfig } from "~/lib/env-validation";
import { formatCurrency, formatDate } from "~/lib/format";

// --- Types ---

interface ContractParty {
	role: string;
	ico: string | null;
	name: string | null;
}

interface Contract {
	id: string;
	externalId: string;
	subject: string | null;
	contractNumber: string | null;
	executionDate: string | null;
	publicationDate: string | null;
	valueWithoutVat: number | null;
	valueWithVat: number | null;
	calculatedPriceCzk: number | null;
	isValid: boolean;
	parties: ContractParty[];
}

export interface ContractsResponse {
	contracts: Contract[];
	total: number;
	page: number;
	limit: number;
	totalValueCzk: number | null;
	supplierCount: number;
	buyerCount: number;
}

export type RoleFilter = "all" | "buyer" | "supplier";

// --- Helpers ---

const ROLE_LABELS: Record<string, string> = {
	supplier: "Dodavatel",
	buyer: "Zadavatel",
};

function getRoleLabel(role: string): string {
	return ROLE_LABELS[role] ?? role;
}

// --- Fetch ---

export async function fetchContracts(
	ico: string,
	page: number,
	role: RoleFilter,
): Promise<ContractsResponse> {
	const params = new URLSearchParams({
		page: String(page),
		limit: "20",
		role,
	});
	const res = await fetch(`${envConfig.apiBaseUrl}/companies/${ico}/contracts?${params}`);
	if (!res.ok) throw new Error("Nepodařilo se načíst smlouvy");
	return res.json();
}

// --- Component ---

interface ContractsTabProps {
	ico: string;
	/** First page (role "all") prefetched server-side by the route loader. */
	initialData?: ContractsResponse;
}

export function ContractsTab({ ico, initialData }: ContractsTabProps) {
	const [page, setPage] = useState(1);
	const [role, setRole] = useState<RoleFilter>("all");

	const query = useQuery({
		queryKey: ["company-contracts", ico, page, role],
		queryFn: () => fetchContracts(ico, page, role),
		initialData: page === 1 && role === "all" ? initialData : undefined,
	});

	if (query.status === "pending") {
		return (
			<div className="flex items-center justify-center py-12">
				<span className="loading loading-spinner loading-lg" />
			</div>
		);
	}

	if (query.status === "error") {
		return (
			<DataLoadError
				title="Smlouvy se nepodařilo načíst"
				description="Filtry i data můžete zkusit načíst znovu."
				onRetry={() => query.refetch()}
				retrying={query.fetchStatus === "fetching"}
			/>
		);
	}

	const { contracts, total, totalValueCzk, supplierCount, buyerCount } = query.data;

	if (total === 0 && role === "all") {
		return (
			<div className="py-8 text-center">
				<Landmark className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
				<p className="text-sm font-medium text-muted-foreground">Žádné smlouvy</p>
				<p className="text-xs text-muted-foreground mt-1">
					V registru smluv nebyly nalezeny žádné smlouvy spojené s touto firmou.
				</p>
			</div>
		);
	}

	const totalPages = Math.ceil(total / 20);

	return (
		<div className="flex flex-col gap-4 pt-4">
			{/* Summary stats */}
			<div className="grid gap-3 grid-cols-2 lg:grid-cols-3">
				<div className="rounded-lg bg-gray-50 p-4">
					<p className="text-xs font-medium text-muted-foreground">Celkem smluv</p>
					<p className="text-2xl font-mono font-semibold tabular-nums">{total}</p>
				</div>
				{totalValueCzk != null && (
					<div className="rounded-lg bg-gray-50 p-4">
						<p className="text-xs font-medium text-muted-foreground">Celková hodnota</p>
						<p className="text-2xl font-mono font-semibold tabular-nums">
							{formatCurrency(totalValueCzk)}
						</p>
					</div>
				)}
				<div className="rounded-lg bg-gray-50 p-4">
					<p className="text-xs font-medium text-muted-foreground">Role</p>
					<p className="text-sm mt-1">
						<span className="font-medium">{supplierCount}</span>
						<span className="text-muted-foreground"> jako dodavatel, </span>
						<span className="font-medium">{buyerCount}</span>
						<span className="text-muted-foreground"> jako zadavatel</span>
					</p>
				</div>
			</div>

			{/* Filter + contract list */}
			<div className="card-surface rounded-xl p-6">
				<div className="flex items-center justify-between mb-4">
					<div className="flex items-center gap-2">
						<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50">
							<Landmark className="h-4 w-4 text-primary-600" />
						</div>
						<h2 className="text-lg font-semibold">Smlouvy v registru</h2>
					</div>

					{/* Role filter */}
					<div className="flex gap-1">
						{(["all", "supplier", "buyer"] as const).map((r) => (
							<button
								key={r}
								type="button"
								onClick={() => {
									setRole(r);
									setPage(1);
								}}
								className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
									role === r
										? "bg-primary text-white"
										: "bg-gray-100 text-gray-600 hover:bg-gray-200"
								}`}
							>
								{r === "all" ? "Vše" : r === "supplier" ? "Dodavatel" : "Zadavatel"}
							</button>
						))}
					</div>
				</div>

				{contracts.length === 0 ? (
					<p className="text-sm text-muted-foreground py-4 text-center">
						Žádné smlouvy pro zvolenou roli.
					</p>
				) : (
					<div className="flex flex-col gap-2">
						{contracts.map((contract) => (
							<ContractRow key={contract.id} contract={contract} companyIco={ico} />
						))}
					</div>
				)}

				{/* Pagination */}
				{totalPages > 1 && (
					<div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
						<p className="text-xs text-muted-foreground">
							Strana {page} z {totalPages} ({total} smluv)
						</p>
						<div className="flex gap-1">
							<button
								type="button"
								disabled={page <= 1}
								onClick={() => setPage((p) => p - 1)}
								className="btn btn-sm btn-secondary-soft"
							>
								Předchozí
							</button>
							<button
								type="button"
								disabled={page >= totalPages}
								onClick={() => setPage((p) => p + 1)}
								className="btn btn-sm btn-secondary-soft"
							>
								Další
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}

function ContractRow({ contract, companyIco }: { contract: Contract; companyIco: string }) {
	const value = contract.calculatedPriceCzk ?? contract.valueWithVat ?? contract.valueWithoutVat;
	const counterparties = contract.parties.filter((p) => p.ico !== companyIco);
	const companyRole = contract.parties.find((p) => p.ico === companyIco)?.role;

	return (
		<div className="flex items-start gap-3 rounded-md border border-gray-100 px-3 py-2.5 hover:bg-gray-50 transition-colors">
			<FileText className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />

			<div className="flex flex-col gap-0.5 min-w-0 flex-1">
				<p className="text-sm font-medium line-clamp-2">
					{contract.subject ?? `Smlouva č. ${contract.contractNumber ?? contract.externalId}`}
				</p>
				<div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
					{companyRole && <span>{getRoleLabel(companyRole)}</span>}
					{counterparties.length > 0 && (
						<>
							<span>·</span>
							<span className="truncate max-w-[200px]">
								{counterparties.map((p) => p.name).join(", ")}
							</span>
						</>
					)}
					{contract.executionDate && (
						<>
							<span>·</span>
							<span>{formatDate(contract.executionDate)}</span>
						</>
					)}
				</div>
			</div>

			{value != null && (
				<span className="text-sm font-mono font-medium tabular-nums whitespace-nowrap">
					{formatCurrency(value)}
				</span>
			)}

			{!contract.isValid && (
				<span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200 whitespace-nowrap">
					Neplatná
				</span>
			)}

			<a
				href={`https://smlouvy.gov.cz/smlouva/${contract.externalId}`}
				target="_blank"
				rel="noopener noreferrer"
				className="btn btn-xs btn-secondary-soft shrink-0 gap-1"
				title="Otevřít v Registru smluv"
			>
				<ExternalLink className="h-3.5 w-3.5" />
				<span className="hidden sm:inline">Detail</span>
			</a>
		</div>
	);
}
